import db from '../db';
import { contentHash } from '../db/contentHash';
import { getLatestBriefingConditional } from './dailyBriefing';
import { getBriefingConditional, getTopicDigestConditional, getTopicDetails } from './topics';
import { getArticleConditional, getArticles, getArticlesBatch } from './articles';
import { getAllHighlightsConditional } from './highlights';
import { writeArticleBatch, BG_FETCH_TAG_PREFIX } from '../sw/download';
import { RECENT_OPENED_LIMIT, ARTICLE_TTL_MS } from '../db/freshness';

// Reason: relative URLs would resolve against the frontend origin (CDN); the
// download phase talks to the backend, so use the same env var as apiClient.
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';

// Budget constants — design §6. OFFLINE_ARTICLE_CAP is a ceiling on the TOTAL
// unique articles; curated (Priority 1) is admitted first and is exempt from
// this cap, fill (Priority 2 topic pages + Priority 3 unread) is admitted only
// until the total reaches the cap.
export const OFFLINE_ARTICLE_CAP = 300;
// Frontend mirror of the backend's BATCH_MAX_IDS (api/articles/crud.py): the
// download phase chunks the article-ID list into batches of this size so each
// GET /api/articles/batch is one bounded IN(...) read (design §8.1).
export const BATCH_MAX_IDS = 50;
// Reason: the offset-0 briefing page is written to the shared Dexie key
// meta['topic-briefing'] that the live read path (Briefing.jsx) reads with
// limit 10. The ETag is derived from the response body, so the prefetch MUST
// request the same limit (10) or the two keys flip-flop content and never 304.
// The "bump to ~20 digests" (design §6 P1) is achieved by walking a second
// offset=10 page rather than fetching limit=20 into the shared key.
export const BRIEFING_PAGE_LIMIT = 10;     // per-request limit; matches reader
export const BRIEFING_MAX_PAGES = 20;      // defensive walk guard (§4.1 caveat)
export const TOPIC_PAGE_TOPICS = 10;       // top-N ranked topics to page (Lever A)
export const TOPIC_PAGE_MAX_PAGES = 2;     // pages 1..2 per topic
export const TOPIC_PAGE_PER_PAGE = 20;     // articles per topic page
export const UNREAD_FILL_PER_PAGE = 100;   // unread fill walk page size (Lever B)
// Reason: TopicDetail opens with showAll=false -> unread_only=true, so the
// default landing mode is 'unread' (see TopicDetail.jsx). We only prefetch the
// default mode to keep the fill budget bounded (design §6 P2).
export const DEFAULT_TOPIC_MODE = 'unread';

// Internal: bounded async worker pool (concurrency n).
async function mapWithConcurrency(n, items, fn, { signal } = {}) {
  let i = 0;
  const workers = Array.from({ length: Math.min(n, items.length) }, async () => {
    while (i < items.length) {
      if (signal?.aborted) return;
      const idx = i++;
      await fn(items[idx], idx);
    }
  });
  await Promise.all(workers);
}

// Internal: collect the curated (Priority 1) article IDs — referenced by daily +
// briefing (one or many pages) + per-topic digest payloads, union with recently
// opened articles. `briefingPayload` accepts a single payload or an array of
// briefing-page payloads (the offset walk produces several).
async function collectArticleIds(dailyPayload, briefingPayload, topicDigestPayloads = []) {
  const ids = new Set();
  (dailyPayload?.themes || []).forEach(t => {
    (t.whats_new || []).forEach(x => x.source_article_id && ids.add(x.source_article_id));
    (t.worth_saving || []).forEach(x => x.source_article_id && ids.add(x.source_article_id));
  });
  const briefingPages = Array.isArray(briefingPayload) ? briefingPayload : [briefingPayload];
  briefingPages.forEach(page => {
    (page?.digests || []).forEach(d => {
      (d.whats_new || []).forEach(x => x.article_id && ids.add(x.article_id));
      (d.recommended_reads || []).forEach(x => x.article_id && ids.add(x.article_id));
    });
  });
  topicDigestPayloads.forEach(d => {
    if (!d) return;
    (d.whats_new || []).forEach(x => x.article_id && ids.add(x.article_id));
    (d.recommended_reads || []).forEach(x => x.article_id && ids.add(x.article_id));
  });
  const recent = await db.articles
    .orderBy('last_opened_at').reverse().limit(RECENT_OPENED_LIMIT).primaryKeys();
  recent.forEach(id => ids.add(id));
  return ids;
}

async function fetchAndStoreDaily() {
  const cached = await db.daily_briefings.get('latest');
  const result = await getLatestBriefingConditional({ etag: cached?.etag ?? null });
  const now = Date.now();
  if (result.status === 304) {
    await db.daily_briefings.update('latest', { fetched_at: now });
    return cached?.payload ?? null;
  }
  // payload === null means 404 (no briefing exists yet) — skip write.
  if (result.payload === null) return null;
  const hash = await contentHash(result.payload);
  await db.daily_briefings.put({
    date: 'latest',
    payload: result.payload,
    etag: result.etag,
    fetched_at: now,
    content_hash: hash,
  });
  return result.payload;
}

// Fetch one page of the topic briefing. offset 0 is stored under the legacy
// meta['topic-briefing'] key (the data-layer read path); later pages are a
// prefetch-only enumeration so they thread a distinct ETag/cache key
// (meta['briefing:p{n}']) — design §8/§9.
async function fetchAndStoreBriefing(offset = 0) {
  const metaKey = offset === 0 ? 'topic-briefing' : `briefing:p${offset / BRIEFING_PAGE_LIMIT + 1}`;
  const cached = await db.meta.get(metaKey);
  const result = await getBriefingConditional({
    etag: cached?.etag ?? null,
    limit: BRIEFING_PAGE_LIMIT,
    include_misc: true,
    offset,
  });
  const now = Date.now();
  if (result.status === 304) {
    await db.meta.update(metaKey, { fetched_at: now });
    return cached?.payload ?? null;
  }
  if (result.payload === null) return null;
  const hash = await contentHash(result.payload);
  await db.meta.put({
    key: metaKey,
    payload: result.payload,
    etag: result.etag,
    fetched_at: now,
    content_hash: hash,
  });
  return result.payload;
}

// Walk briefing pages via the offset param until exhausted or no new digests
// are seen. Runs sequentially (page N+1 depends on page N), holding <=1 of the
// global concurrency-2 budget. Returns the list of page payloads.
async function fetchBriefingPages({ signal, onError } = {}) {
  const payloads = [];
  const seenTopics = new Set();
  let offset = 0;
  let pages = 0;
  while (!signal?.aborted && pages < BRIEFING_MAX_PAGES) {
    let payload = null;
    try {
      payload = await fetchAndStoreBriefing(offset);
    } catch {
      onError?.();
      break;
    }
    const digests = payload?.digests || [];
    if (digests.length === 0) break;
    const fresh = digests.filter(d => d.topic_id && !seenTopics.has(d.topic_id));
    // After the first page, stop once a page yields no new digests.
    if (offset > 0 && fresh.length === 0) break;
    digests.forEach(d => d.topic_id && seenTopics.add(d.topic_id));
    payloads.push(payload);
    pages += 1;
    if (digests.length < BRIEFING_PAGE_LIMIT) break;  // last page
    offset += BRIEFING_PAGE_LIMIT;
  }
  return payloads;
}

async function fetchAndStoreArticle(id) {
  const cached = await db.articles.get(id);
  const result = await getArticleConditional(id, { etag: cached?.etag ?? null });
  const now = Date.now();
  if (result.status === 304) {
    await db.articles.update(id, { fetched_at: now });
    return;
  }
  if (result.payload === null) return;
  const hash = await contentHash(result.payload);
  await db.articles.put({
    id,
    payload: result.payload,
    etag: result.etag,
    fetched_at: now,
    content_hash: hash,
    last_opened_at: cached?.last_opened_at ?? null,
    is_read: result.payload.is_read ? 1 : 0,
  });
}

// Returns the stored payload (fresh or cached) so the caller can collect article IDs.
async function fetchAndStoreTopicDigest(topicId) {
  const cached = await db.topic_digests.get(topicId);
  const result = await getTopicDigestConditional(topicId, { etag: cached?.etag ?? null });
  const now = Date.now();
  if (result.status === 304) {
    await db.topic_digests.update(topicId, { fetched_at: now });
    return cached?.payload ?? null;
  }
  if (result.payload === null) return null;
  const hash = await contentHash(result.payload);
  await db.topic_digests.put({
    topic_id: topicId,
    payload: result.payload,
    etag: result.etag,
    fetched_at: now,
    content_hash: hash,
  });
  return result.payload;
}

// Priority 2 (Lever A): fetch one topic-detail page in the default landing mode,
// write a lightweight page-index row to paged_views (ordered article IDs + topic/
// pagination metadata — NOT duplicated article content, design §7), and return
// the page total so the caller can decide whether to page again.
//
// `admit(id)` admits the ID into the download set (respecting the fill cap) and
// returns whether the ID ended up in the set. Only ADMITTED IDs are written to
// the page index, so an offline page never references an article that was never
// downloaded (design §7.1 — index commit implies entity present in the set).
async function fetchAndStoreTopicPage(topicId, page, admit, mode = DEFAULT_TOPIC_MODE) {
  const offset = (page - 1) * TOPIC_PAGE_PER_PAGE;
  const data = await getTopicDetails(topicId, {
    unread_only: mode === 'unread',
    limit: TOPIC_PAGE_PER_PAGE,
    offset,
  });
  const returned = (data?.articles || []).filter(a => a?.id != null);
  // Reason: the page index stores lightweight list-ROW snapshots (display fields
  // only — title/summary/is_read/url/etc., NEVER full article content) so the
  // offline TopicDetail list renders directly from the index. Full content stays
  // single-sourced in `articles` (downloaded separately), so ArticleViewer's
  // offline "not downloaded" card is never bypassed by a body-less row.
  const articles = returned.filter(a => admit(a.id));  // only within-budget rows
  const articleIds = articles.map(a => a.id);
  const payload = {
    articleIds,
    articles,
    label: data?.label ?? null,
    unread_count: data?.unread_count ?? null,
    article_count: data?.article_count ?? null,
    pagination: data?.pagination ?? null,
    total_articles: data?.total_articles ?? returned.length,
    // Reason: the offline TopicDetail header date + metadata panel render from
    // these (MAJOR-2). This is the page actually read offline, so the snapshot
    // must carry them or the offline view degrades (broken date, no metadata).
    created_at: data?.created_at ?? null,
    topic_metadata: data?.topic_metadata ?? null,
    algorithm: data?.algorithm ?? null,
  };
  // Reason: pageSize is in the key (MINOR-3) and the prefetch only caches the
  // size-20 page; a reader at a different pageSize uses a different key.
  const key = `topic:${topicId}:p${page}:${mode}:s${TOPIC_PAGE_PER_PAGE}`;
  const hash = await contentHash(payload);
  await db.paged_views.put({
    key,
    payload,
    etag: null,
    fetched_at: Date.now(),
    content_hash: hash,
  });
  return { totalArticles: payload.total_articles };
}

// Priority 3 (Lever B): walk the global article list (unread-FIRST ordering) and
// admit only unread IDs via `admit`, stopping at the unread->read boundary, the
// last page, abort, or when `admit` reports the cap is reached. Sequential walk
// — holds <=1 of the global concurrency-2 budget.
async function walkUnreadFill(admit, { signal, onError } = {}) {
  let page = 1;
  while (!signal?.aborted) {
    let data;
    try {
      data = await getArticles({ page, per_page: UNREAD_FILL_PER_PAGE });
    } catch {
      onError?.();
      break;
    }
    const list = data?.articles || [];
    if (list.length === 0) break;
    let capReached = false;
    let hitReadBoundary = false;
    for (const a of list) {
      if (a.is_read) { hitReadBoundary = true; break; }  // unread backlog exhausted
      const admitted = admit(a.id);
      if (admitted === false) { capReached = true; break; }
    }
    if (capReached || hitReadBoundary) break;
    const totalPages = data?.total_pages ?? page;
    if (page >= totalPages) break;
    page += 1;
  }
}

// Drop candidate IDs already fresh in db.articles (present AND within
// ARTICLE_TTL_MS) so the download fetches only what's genuinely needed
// (design §6 conditional-filter).
async function filterFreshArticles(ids) {
  if (ids.length === 0) return [];
  const now = Date.now();
  const rows = await db.articles.bulkGet(ids);
  const out = [];
  ids.forEach((id, i) => {
    const row = rows[i];
    const fresh = row && row.fetched_at != null && now - row.fetched_at < ARTICLE_TTL_MS;
    if (!fresh) out.push(id);
  });
  return out;
}

async function fetchAndStoreHighlights() {
  const cached = await db.meta.get('all-highlights');
  const result = await getAllHighlightsConditional({ page: 1, per_page: 50, etag: cached?.etag ?? null });
  const now = Date.now();
  if (result.status === 304) {
    await db.meta.update('all-highlights', { fetched_at: now });
    return;
  }
  if (result.payload === null) return;
  const hash = await contentHash(result.payload);
  await db.meta.put({
    key: 'all-highlights',
    payload: result.payload,
    etag: result.etag,
    fetched_at: now,
    content_hash: hash,
  });
}

function done(progress) {
  return { ...progress, phase: 'done' };
}

// ---- DOWNLOAD phase (design §8.5) ----------------------------------------

// Split an array into fixed-size chunks (<= BATCH_MAX_IDS per batch request).
function chunkIds(ids, size) {
  const out = [];
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
  return out;
}

// Background Fetch is Chromium/Android only; iOS Safari lacks it (design §8.5).
// Reason: detect the actual registration PROPERTY we use (reg.backgroundFetch),
// NOT the global interface name `BackgroundFetchManager` — the latter is never a
// property of ServiceWorkerRegistration.prototype, so checking it returns false
// even in Chrome and the survives-app-close path would never run (per MDN, the
// feature is exposed as `ServiceWorkerRegistration.prototype.backgroundFetch`).
function hasBackgroundFetch() {
  return typeof ServiceWorkerRegistration !== 'undefined'
    && 'backgroundFetch' in ServiceWorkerRegistration.prototype;
}

// Register ONE Background Fetch carrying the batch GET requests. The browser
// downloads them, shows OS progress, and CONTINUES AFTER THE APP IS CLOSED; the
// service worker's backgroundfetchsuccess handler writes the responses into
// db.articles via the shared writer.
async function registerBackgroundFetch(ids) {
  const reg = await navigator.serviceWorker?.ready;
  if (!reg?.backgroundFetch?.fetch) throw new Error('backgroundFetch unavailable');
  // Reason: bake the CURRENT localStorage auth token into each batch Request.
  // This codebase has no foreground token-expiry/refresh infra (only the SW's
  // reactive 401 handler for mutations), so the spec's "refresh-if-near-expiry
  // before registering" is not implementable here. A 401'd batch simply leaves
  // those articles un-fetched; the foreground reconciler (next runPrefetch)
  // refills them with full Phase-3 401 handling via apiClient. (Design §8.5
  // auth exception — task-resolved contradiction.)
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('authToken')) || '';
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const requests = chunkIds(ids, BATCH_MAX_IDS).map(chunk =>
    // Reason: repeated `ids=` keys — FastAPI's List[int] Query rejects a
    // comma-joined value (422); see getArticlesBatch in services/articles.js.
    new Request(`${API_BASE}/api/articles/batch?${chunk.map(id => `ids=${id}`).join('&')}`, { headers })
  );
  const tag = `${BG_FETCH_TAG_PREFIX}${Date.now()}`;
  await reg.backgroundFetch.fetch(tag, requests, {
    title: `Downloading ${ids.length} articles for offline reading`,
    downloadTotal: 0,
  });
}

// iOS / no-Background-Fetch fallback: foreground-fetch the same batch requests
// at concurrency 2 (the resolve-phase budget) and write via the shared writer.
// A batch failure falls back to the per-item reconciler (full Phase-3 401
// handling via apiClient) so the failure recovers in place.
async function foregroundBatchDownload(ids, { signal, onItem, onError } = {}) {
  const chunks = chunkIds(ids, BATCH_MAX_IDS);
  await mapWithConcurrency(2, chunks, async (chunk) => {
    if (signal?.aborted) return;
    try {
      const articles = await getArticlesBatch(chunk);
      await writeArticleBatch(articles);
      // Reason: count only what the server actually returned (missing/forbidden
      // ids are omitted, not errors), so fetched never over-reports.
      onItem?.(articles.length);
    } catch {
      // Reason: a failed batch is NOT itself an error — it is about to be
      // retried per-item by the reconciler. Only an item that ends the run
      // still missing (its per-item fetch also throws) counts toward errors,
      // and fetched advances only by items actually written.
      let written = 0;
      for (const id of chunk) {
        if (signal?.aborted) break;
        try {
          await fetchAndStoreArticle(id);
          written++;
        } catch {
          onError?.();
        }
      }
      onItem?.(written);
    }
  }, { signal });
}

// At most one auto-resume listener set is active at a time: starting a new
// download run supersedes the previous run's resumer (stale once a fresh run
// has re-resolved the id list).
let activeAutoResumeCleanup = null;
// Promise of the prior resumer's in-flight foreground pass (or null). A new
// run awaits this AFTER aborting, so the prior pool fully drains before the new
// pool starts — global foreground concurrency never exceeds 2 across runs.
let activeResumeInFlight = null;
function cancelActiveAutoResume() {
  if (activeAutoResumeCleanup) {
    const fn = activeAutoResumeCleanup;
    activeAutoResumeCleanup = null;
    fn(); // removes listeners AND aborts the resumer's internal signal
  }
  return activeResumeInFlight;
}

// Re-trigger the foreground fallback when the app returns to the foreground or
// reconnects, until every id is cached or the run is aborted. Re-runs are cheap
// because filterFreshArticles skips already-downloaded ids (design §8.5).
function setupAutoResume(ids, { signal, onItem, onError } = {}) {
  if (typeof window === 'undefined' || signal?.aborted) return;
  let running = false;
  // Reason: the resumer owns an internal AbortController chained to the external
  // signal so that cleanup() (called by cancelActiveAutoResume on a new run)
  // actually STOPS an in-flight foreground pass — not just its listeners. Without
  // this, a superseded resumer's concurrency-2 pool keeps running and a new run's
  // pool stacks on top, breaching the <=2 db-f1-micro budget.
  const ac = new AbortController();
  const cleanup = () => {
    ac.abort();
    window.removeEventListener('online', trigger);
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisible);
    }
    signal?.removeEventListener?.('abort', cleanup);
    if (activeAutoResumeCleanup === cleanup) activeAutoResumeCleanup = null;
  };
  const trigger = async () => {
    if (running || ac.signal.aborted) return;
    running = true;
    const pass = (async () => {
      const missing = await filterFreshArticles(ids);
      if (missing.length === 0) { cleanup(); return; }
      await foregroundBatchDownload(missing, { signal: ac.signal, onItem, onError });
      const stillMissing = await filterFreshArticles(ids);
      if (stillMissing.length === 0) cleanup();
    })();
    activeResumeInFlight = pass;
    try {
      await pass;
    } catch {
      onError?.();
    } finally {
      running = false;
      if (activeResumeInFlight === pass) activeResumeInFlight = null;
    }
  };
  const onVisible = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') trigger();
  };
  window.addEventListener('online', trigger);
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisible);
  }
  // External abort tears down the whole resumer (listeners + in-flight pass).
  signal?.addEventListener?.('abort', cleanup);
  activeAutoResumeCleanup = cleanup;
}

/**
 * Download full article content for the resolve-phase ID list into db.articles.
 *
 * Platform-split (design §8.5): Background Fetch on Android/Chromium (survives
 * the app being closed), foreground batch download with auto-resume on iOS / no
 * Background Fetch. Both paths write identical db.articles rows, so offline
 * reads don't care which ran.
 *
 * @param {number[]} ids - Resolve-phase article IDs to download.
 * @param {object} [opts]
 * @param {AbortSignal} [opts.signal]
 * @param {Function} [opts.onItem] - Called with the count fetched per batch.
 * @param {Function} [opts.onError]
 */
export async function downloadArticles(ids, { signal, onItem, onError } = {}) {
  if (!ids || ids.length === 0) return;
  // A fresh download run supersedes any prior run's auto-resume: abort it AND
  // wait for its in-flight foreground pass to fully drain before we start ours,
  // so the two pools never overlap past the <=2 concurrency budget.
  const priorResume = cancelActiveAutoResume();
  if (priorResume) {
    try { await priorResume; } catch { /* prior run's failure is its own */ }
  }

  // Persistent-storage precondition (design §8.5): verify (don't assume) before
  // filling the larger cache, or the browser may silently evict it under
  // pressure. requestPersistOnce is idempotent and the UI surfaces the "storage
  // not persistent" hint on denial; here we proceed regardless of the grant.
  try {
    await requestPersistOnce();
  } catch {
    /* non-fatal — proceed */
  }
  if (signal?.aborted) return;

  if (hasBackgroundFetch()) {
    try {
      await registerBackgroundFetch(ids);
      // The browser now owns the download and the SW writes results. The
      // foreground reconciler (next runPrefetch) refills anything it missed —
      // so we must NOT mark these articles present here.
      return;
    } catch {
      onError?.();
      // Registration failed — fall through to the foreground path.
    }
  }

  await foregroundBatchDownload(ids, { signal, onItem, onError });
  if (signal?.aborted) return;
  // Only arm auto-resume if something is still missing — a fully-successful
  // pass leaves no listeners attached.
  let remaining = [];
  try {
    remaining = await filterFreshArticles(ids);
  } catch {
    remaining = ids;
  }
  if (remaining.length > 0) setupAutoResume(ids, { signal, onItem, onError });
}

/**
 * Run the full prefetch as a RESOLVE phase (build a prioritized, deduped,
 * cap-bounded, freshness-filtered article-ID list + write page indexes) followed
 * by a foreground DOWNLOAD of that list — design §6.
 *
 * All resolve walks share a single global concurrency budget of 2 (they run
 * sequentially; never a second parallel pool), so prefetch holds <=2 of
 * db-f1-micro's ~22 connection slots.
 *
 * @param {object} [opts]
 * @param {AbortSignal} [opts.signal] - Optional abort signal for lifecycle cleanup.
 * @param {Function} [opts.onProgress] - Called with progress object on each state change.
 * @returns {Promise<{phase:'done', fetched:number, total:number, errors:number}>}
 */
export async function runPrefetch({ signal, onProgress } = {}) {
  const progress = { phase: 'daily', fetched: 0, total: 0, errors: 0 };
  const emit = () => onProgress?.({ ...progress });
  emit();

  // ---- RESOLVE: build the ordered, deduped candidate list ----
  // Priority 1 (curated) is admitted with `admitCurated` (exempt from the cap);
  // Priority 2/3 fill via `admitFill` which rejects once the total hits the cap.
  const candidates = [];
  const seen = new Set();
  const admitCurated = (id) => {
    if (id == null || seen.has(id)) return;
    seen.add(id);
    candidates.push(id);
  };
  // Returns false once the cap is reached (including the admit that fills it) so
  // a sequential walk stops on the page that exhausts the budget — never paging
  // one further (design §10).
  const admitFill = (id) => {
    if (seen.size >= OFFLINE_ARTICLE_CAP) return false;
    if (id != null && !seen.has(id)) {
      seen.add(id);
      candidates.push(id);
    }
    return seen.size < OFFLINE_ARTICLE_CAP;
  };
  // Like admitFill, but returns whether the ID is actually in the download set
  // (admitted now or already present) — used by the topic page index so it lists
  // exactly the articles that get downloaded, never any dropped by the cap.
  const admitToSet = (id) => {
    admitFill(id);
    return id != null && seen.has(id);
  };
  const budgetFull = () => seen.size >= OFFLINE_ARTICLE_CAP;

  // Phase 1: daily briefing (curated)
  let dailyPayload = null;
  try {
    dailyPayload = await fetchAndStoreDaily();
  } catch {
    progress.errors++;
  }
  if (signal?.aborted) return done(progress);

  // Phase 2: unified topic briefing — walk pages via offset (curated)
  progress.phase = 'digests';
  emit();
  let briefingPages = [];
  try {
    briefingPages = await fetchBriefingPages({ signal, onError: () => progress.errors++ });
  } catch {
    progress.errors++;
  }
  if (signal?.aborted) return done(progress);

  // Phase 2b: per-topic digests (populates db.topic_digests for TopicDetail offline)
  const topicIds = [];
  briefingPages.forEach(p => (p?.digests || []).forEach(d => {
    if (d.topic_id && !topicIds.includes(d.topic_id)) topicIds.push(d.topic_id);
  }));
  const topicDigestPayloads = [];
  await mapWithConcurrency(2, topicIds, async (topicId) => {
    if (signal?.aborted) return;
    try {
      const payload = await fetchAndStoreTopicDigest(topicId);
      if (payload) topicDigestPayloads.push(payload);
    } catch {
      progress.errors++;
    }
  }, { signal });
  if (signal?.aborted) return done(progress);

  // Admit Priority 1 curated IDs (exempt from the cap).
  const curated = await collectArticleIds(dailyPayload, briefingPages, topicDigestPayloads);
  curated.forEach(admitCurated);

  // Phase 3 (Priority 2 — Lever A): top-topic detail pages → page indexes + IDs.
  progress.phase = 'topic_pages';
  emit();
  const topPageTopics = topicIds.slice(0, TOPIC_PAGE_TOPICS);
  await mapWithConcurrency(2, topPageTopics, async (topicId) => {
    for (let page = 1; page <= TOPIC_PAGE_MAX_PAGES; page++) {
      if (signal?.aborted) return;
      if (budgetFull()) return;  // no fill budget left — don't fetch a page we can't admit
      try {
        // fetchAndStoreTopicPage admits via admitToSet and writes only the
        // admitted (within-budget) subset to the page index.
        const { totalArticles } = await fetchAndStoreTopicPage(topicId, page, admitToSet);
        // Only page further when the topic has more than one page of articles.
        if (totalArticles <= page * TOPIC_PAGE_PER_PAGE) return;
      } catch {
        progress.errors++;
        return;
      }
    }
  }, { signal });
  if (signal?.aborted) return done(progress);

  // Phase 4 (Priority 3 — Lever B): global unread fill until the cap is reached.
  // Skip entirely if curated + topic pages already exhausted the budget.
  progress.phase = 'unread_fill';
  emit();
  if (!budgetFull()) {
    await walkUnreadFill(admitFill, { signal, onError: () => progress.errors++ });
  }
  if (signal?.aborted) return done(progress);

  // Conditional-filter: drop IDs already fresh in db.articles.
  let downloadIds = [];
  try {
    downloadIds = await filterFreshArticles(candidates);
  } catch {
    progress.errors++;
    downloadIds = candidates;
  }

  // ---- DOWNLOAD: Background Fetch (Android) or foreground batch (iOS) ----
  progress.phase = 'articles';
  progress.total = downloadIds.length;
  progress.fetched = 0;
  emit();

  await downloadArticles(downloadIds, {
    signal,
    onItem: (n) => {
      progress.fetched = Math.min(progress.fetched + n, progress.total);
      emit();
    },
    onError: () => { progress.errors++; },
  });

  if (signal?.aborted) return done(progress);

  // Phase 5: highlights
  progress.phase = 'highlights';
  emit();
  try {
    await fetchAndStoreHighlights();
  } catch {
    progress.errors++;
  }

  progress.phase = 'done';
  emit();
  return { ...progress };
}

/**
 * Request persistent storage once; records the result in meta['storage_persist'].
 * Idempotent — returns the cached result on subsequent calls.
 *
 * @returns {Promise<{granted:boolean, denied:boolean, alreadyAsked:boolean}>}
 */
export async function requestPersistOnce() {
  const row = await db.meta.get('storage_persist');
  if (row) {
    return {
      granted: !!row.value?.granted,
      denied: !row.value?.granted,
      alreadyAsked: true,
    };
  }
  if (!navigator?.storage?.persist) {
    await db.meta.put({ key: 'storage_persist', value: { granted: false, asked_at: Date.now() } });
    return { granted: false, denied: true, alreadyAsked: false };
  }
  const granted = await navigator.storage.persist();
  await db.meta.put({ key: 'storage_persist', value: { granted, asked_at: Date.now() } });
  return { granted, denied: !granted, alreadyAsked: false };
}

// Exported for unit tests only.
export const __test__ = {
  mapWithConcurrency,
  collectArticleIds,
  fetchAndStoreTopicDigest,
  fetchAndStoreTopicPage,
  walkUnreadFill,
  filterFreshArticles,
  fetchBriefingPages,
  chunkIds,
  hasBackgroundFetch,
  foregroundBatchDownload,
  downloadArticles,
};
